import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import { useIsMobile } from '../../hooks/useIsMobile';
import { Icons } from '../Icons';
import { StorageService } from '../../services/storage';
import { setLastSeenVersion } from '../../config/version';
import './OnboardingModal.css';

interface FeatureItem {
  icon: React.ComponentType<{ size?: number; style?: React.CSSProperties }>;
  label: string;
  description: string;
}

interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  illustration: string;
  gradient?: string;
  features?: FeatureItem[];
  tips?: string[];
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface OnboardingModalProps {
  onComplete: () => void;
  onAddToken?: () => void;
}

export function OnboardingModal({ onComplete, onAddToken }: OnboardingModalProps) {
  const { t } = useTranslation();
   
  const { language: _lang } = useLanguage(); // Force re-render on language change
  const isMobile = useIsMobile();

  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  // Touch/swipe handling for mobile
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);
  const minSwipeDistance = 50; // Minimum distance for a swipe

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchEndX.current = null;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;

    const distance = touchStartX.current - touchEndX.current;
    const isSwipeLeft = distance > minSwipeDistance;
    const isSwipeRight = distance < -minSwipeDistance;

    if (isSwipeLeft && currentStep < steps.length - 1) {
      // Swipe left = next slide
      setCurrentStep(currentStep + 1);
    } else if (isSwipeRight && currentStep > 0) {
      // Swipe right = previous slide
      setCurrentStep(currentStep - 1);
    }

    // Reset
    touchStartX.current = null;
    touchEndX.current = null;
  };

  // Build steps with translations - Consolidated to 5 essential steps
  const getSteps = (): OnboardingStep[] => [
    // Step 1: Welcome - Key features overview
    {
      id: 'welcome',
      title: t('onboarding.steps.welcome.title'),
      description: t('onboarding.steps.welcome.description'),
      illustration: '💼',
      gradient: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1), rgba(139, 92, 246, 0.1))',
      features: [
        {
          icon: Icons.TrendingUp,
          label: t('onboarding.steps.welcome.features.fifoLifo.label'),
          description: t('onboarding.steps.welcome.features.fifoLifo.description')
        },
        {
          icon: Icons.Repeat,
          label: t('onboarding.steps.welcome.features.swapTracking.label'),
          description: t('onboarding.steps.welcome.features.swapTracking.description')
        },
        {
          icon: Icons.Database,
          label: t('onboarding.steps.welcome.features.miningStaking.label'),
          description: t('onboarding.steps.welcome.features.miningStaking.description')
        }
      ],
    },
    // Step 2: Add your first token
    {
      id: 'add-token',
      title: t('onboarding.steps.addToken.title'),
      description: t('onboarding.steps.addToken.description'),
      illustration: '💰',
      gradient: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(5, 150, 105, 0.1))',
      tips: [
        t('onboarding.steps.addToken.tips.search'),
        t('onboarding.steps.addToken.tips.transactions'),
        t('onboarding.steps.addToken.tips.fifoAuto')
      ],
      action: onAddToken ? {
        label: t('onboarding.steps.addToken.actionLabel'),
        onClick: () => {
          onAddToken();
          handleNext();
        },
      } : undefined,
    },
    // Step 3: AI Trading Bot
    {
      id: 'ai-bot',
      title: t('onboarding.steps.aiBot.title'),
      description: t('onboarding.steps.aiBot.description'),
      illustration: '🤖',
      gradient: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1), rgba(217, 119, 6, 0.1))',
      features: [
        {
          icon: Icons.Zap,
          label: t('onboarding.steps.aiBot.features.signals.label'),
          description: t('onboarding.steps.aiBot.features.signals.description')
        },
        {
          icon: Icons.BarChart,
          label: t('onboarding.steps.aiBot.features.backtesting.label'),
          description: t('onboarding.steps.aiBot.features.backtesting.description')
        },
        {
          icon: Icons.TrendingUp,
          label: t('onboarding.steps.aiBot.features.sentiment.label'),
          description: t('onboarding.steps.aiBot.features.sentiment.description')
        }
      ],
    },
    // Step 4: Modules & Tools (consolidated)
    {
      id: 'modules',
      title: t('onboarding.steps.modulesMonitoring.title'),
      description: t('onboarding.steps.modulesMonitoring.description'),
      illustration: '🧩',
      gradient: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(37, 99, 235, 0.1))',
      features: [
        {
          icon: Icons.Shield,
          label: t('onboarding.steps.seedVault.features.aes.label'),
          description: t('onboarding.steps.seedVault.features.aes.description')
        },
        {
          icon: Icons.Server,
          label: t('onboarding.steps.modulesMonitoring.features.fluxnode.label'),
          description: t('onboarding.steps.modulesMonitoring.features.fluxnode.description')
        },
        {
          icon: Icons.TrendingUp,
          label: t('onboarding.steps.bitcoinCycles.features.phases.label'),
          description: t('onboarding.steps.bitcoinCycles.features.phases.description')
        }
      ],
    },
    // Step 5: Get Started
    {
      id: 'get-started',
      title: t('onboarding.steps.swissArmyKnife.title'),
      description: t('onboarding.steps.swissArmyKnife.description'),
      illustration: '🚀',
      gradient: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(124, 58, 237, 0.1))',
      tips: [
        '⌘K ' + t('onboarding.steps.swissArmyKnife.features.modular.description'),
        t('onboarding.steps.swissArmyKnife.features.customize.description'),
        t('onboarding.steps.swissArmyKnife.features.exception.description')
      ],
    },
  ];

  const steps = getSteps();

  useEffect(() => {
    // Show modal with animation delay
    setTimeout(() => setIsVisible(true), 100);
  }, []);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    setIsVisible(false);
    setTimeout(() => {
      StorageService.set('onboarding_completed', true);
      // Mark current version as seen so "What's New" won't show right after onboarding
      setLastSeenVersion();
      onComplete();
    }, 300);
  };

  const handleComplete = () => {
    setIsVisible(false);
    setTimeout(() => {
      StorageService.set('onboarding_completed', true);
      // Mark current version as seen so "What's New" won't show right after onboarding
      setLastSeenVersion();
      onComplete();
    }, 300);
  };

  const step = steps[currentStep];
  const progress = ((currentStep + 1) / steps.length) * 100;

  return (
    <div className={`onboarding-overlay ${isVisible ? 'visible' : ''}`}>
      <div
        className={`onboarding-modal ${isVisible ? 'visible' : ''}`}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Close button */}
        <button
          className="onboarding-close"
          onClick={handleSkip}
          aria-label="Skip onboarding"
        >
          <Icons.X size={20} />
        </button>

        {/* Progress bar */}
        <div className="onboarding-progress">
          <div
            className="onboarding-progress-bar"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Step indicator */}
        <div className="onboarding-step-indicator">
          {currentStep + 1} / {steps.length}
        </div>

        {/* Content */}
        <div className="onboarding-content">
          <div
            className="onboarding-illustration animate-scale-in"
            style={{
              background: step.gradient || 'linear-gradient(135deg, rgba(99, 102, 241, 0.1), rgba(139, 92, 246, 0.1))',
              borderRadius: '50%',
              width: '120px',
              height: '120px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '48px',
              margin: '0 auto 24px'
            }}
          >
            {step.illustration}
          </div>

          <h2 className="onboarding-title">{step.title}</h2>

          <p className="onboarding-description">{step.description}</p>

          {/* Features List */}
          {step.features && step.features.length > 0 && (
            <div className="stagger-children" style={{
              marginTop: '24px',
              display: 'grid',
              gap: '16px',
              textAlign: 'left',
              padding: '0 20px'
            }}>
              {step.features.map((feature, idx) => (
                <div key={idx} className="animate-fade-in-left" style={{
                  display: 'flex',
                  gap: '12px',
                  alignItems: 'flex-start'
                }}>
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '8px',
                    background: 'var(--accent)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    color: 'white'
                  }}>
                    <feature.icon size={16} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontWeight: 600,
                      fontSize: '14px',
                      marginBottom: '2px',
                      color: 'var(--text)'
                    }}>
                      {feature.label}
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: 'var(--muted)',
                      lineHeight: '1.4'
                    }}>
                      {feature.description}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Tips Section */}
          {step.tips && step.tips.length > 0 && (
            <div style={{
              marginTop: '24px',
              padding: '16px',
              background: 'rgba(59, 130, 246, 0.05)',
              borderRadius: '12px',
              borderLeft: '3px solid #3b82f6',
              textAlign: 'left'
            }}>
              <div style={{
                fontSize: '13px',
                fontWeight: 600,
                marginBottom: '12px',
                color: '#3b82f6',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <Icons.Lightbulb size={16} />
                {t('onboarding.navigation.tips')}
              </div>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0,
                display: 'grid',
                gap: '8px'
              }}>
                {step.tips.map((tip, idx) => (
                  <li key={idx} style={{
                    fontSize: '12px',
                    color: 'var(--muted)',
                    lineHeight: '1.5',
                    paddingLeft: '16px',
                    position: 'relative'
                  }}>
                    <span style={{
                      position: 'absolute',
                      left: 0,
                      color: '#3b82f6'
                    }}>
                      •
                    </span>
                    {tip}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {step.action && (
            <button
              className="onboarding-action-button"
              onClick={step.action.onClick}
              style={{ marginTop: '24px' }}
            >
              {step.action.label}
            </button>
          )}
        </div>

        {/* Navigation - Simplified on mobile (arrows + dots), full on desktop */}
        {isMobile ? (
          <div className="onboarding-mobile-footer">
            {/* Mobile: Compact navigation with arrows and dots */}
            <div className="onboarding-navigation-mobile">
              <button
                className="onboarding-arrow-button"
                onClick={handlePrevious}
                disabled={currentStep === 0}
                aria-label="Previous"
              >
                <Icons.ChevronLeft size={24} />
              </button>

              <div className="onboarding-dots">
                {steps.map((_, index) => (
                  <button
                    key={index}
                    className={`onboarding-dot ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
                    onClick={() => setCurrentStep(index)}
                    aria-label={`Go to step ${index + 1}`}
                  />
                ))}
              </div>

              <button
                className="onboarding-arrow-button"
                onClick={currentStep < steps.length - 1 ? handleNext : handleComplete}
                aria-label={currentStep < steps.length - 1 ? "Next" : "Start"}
              >
                {currentStep < steps.length - 1 ? (
                  <Icons.ChevronRight size={24} />
                ) : (
                  <Icons.Check size={24} />
                )}
              </button>
            </div>

            {/* Skip link */}
            <button className="onboarding-skip" onClick={handleSkip}>
              {t('onboarding.navigation.skipForever')}
            </button>
          </div>
        ) : (
          <>
            {/* Desktop: Full navigation buttons */}
            <div className="onboarding-navigation">
              <button
                className="onboarding-nav-button secondary"
                onClick={handlePrevious}
                disabled={currentStep === 0}
              >
                <Icons.ChevronLeft size={20} />
                {t('onboarding.navigation.previous')}
              </button>

              {currentStep < steps.length - 1 ? (
                <button
                  className="onboarding-nav-button primary"
                  onClick={handleNext}
                >
                  {t('onboarding.navigation.next')}
                  <Icons.ChevronRight size={20} />
                </button>
              ) : (
                <button
                  className="onboarding-nav-button primary"
                  onClick={handleComplete}
                >
                  {t('onboarding.navigation.letsGo')}
                </button>
              )}
            </div>

            {/* Skip link */}
            <button className="onboarding-skip" onClick={handleSkip}>
              {t('onboarding.navigation.skipForever')}
            </button>

            {/* Dots navigation */}
            <div className="onboarding-dots">
              {steps.map((_, index) => (
                <button
                  key={index}
                  className={`onboarding-dot ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
                  onClick={() => setCurrentStep(index)}
                  aria-label={`Go to step ${index + 1}`}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
